import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import api from '@/utils/axios'
import router from '@/router'

const FILE_BASE = String(import.meta.env.VITE_FILE_BASE_URL || '').replace(/\/$/, '')
const DEFAULT_AVATAR = '/default-avatar.png'

function resolveAvatar(file_user) {
  if (!file_user) return DEFAULT_AVATAR

  // Jika URL sudah absolut, langsung return
  if (file_user.startsWith('http://') || file_user.startsWith('https://')) {
    return file_user
  }

  // Jika storage path, gabung dengan FILE_BASE
  return `${FILE_BASE}/${file_user.replace(/^\/storage\//, '')}`
}

export const useAuthStore = defineStore("auth", () => {
  const user = ref(null)
  const token = ref(localStorage.getItem("token"))
  const loading = ref(false)
  const forceChangePassword = ref(false)

  // Perbaikan: computed property yang memeriksa token dan user
  const isLoggedIn = computed(() => Boolean(token.value && user.value?.id))

  function normalizePermissions(rawPerms) {
    if (!rawPerms) return []

    if (Array.isArray(rawPerms)) {
      return rawPerms
        .flatMap(p => {
          if (!p) return []

          // kalau sudah string
          if (typeof p === 'string') return p

          // kalau object: ambil name & display_name
          const names = []
          if (typeof p.name === 'string') names.push(p.name)
          if (typeof p.display_name === 'string') names.push(p.display_name)
          return names
        })
        .map(s => s.trim())
        .filter(Boolean)
    }

    // jaga-jaga kalau suatu hari backend kirim string join
    if (typeof rawPerms === 'string') {
      return rawPerms
        .split('|')
        .map(s => s.trim())
        .filter(Boolean)
    }

    return []
  }

  const hasPermission = (permissionName) => {
    if (!isLoggedIn.value || !Array.isArray(user.value?.permissions)) return false
    return user.value.permissions.includes(permissionName)
  }


  // const setUser = (userData) => {
  //   if (!userData) {
  //     user.value = null
  //     localStorage.removeItem('user')
  //     return
  //   }

  //   const raw = Array.isArray(userData) ? userData[0] : userData

  //   // Simpan file_user original dari API
  //   const avatar_url = raw.file_user || raw.avatar || raw.photo || null
  //   console.log('[auth] Raw avatar URL:', avatar_url)
  //   const perms = normalizePermissions(raw.permissions)

  //   user.value = {
  //     id: raw.id || raw.user_id || raw.id_user || null,
  //     name: raw.name || raw.nama || raw.username || null,
  //     email: raw.email || null,
  //     file_user: avatar_url, // simpan URL original
  //     // permissions: Array.isArray(raw.permissions) ? raw.permissions : [],
  //     permissions: perms,  
  //     roles: Array.isArray(raw.roles) ? raw.roles : [],
  //     ...raw
  //   }
  //   console.log('[auth] permissions normalized:', user.value.permissions)
  //   console.log('[auth] Resolved avatar:', resolveAvatar(avatar_url))
  //   localStorage.setItem("user", JSON.stringify(user.value))
  // }

  const setUser = (userData) => {
    if (!userData) {
      user.value = null
      localStorage.removeItem('user')
      return
    }

    const raw = Array.isArray(userData) ? userData[0] : userData

    const avatar_url = raw.file_user || raw.avatar || raw.photo || null

    // 🔹 permissions yang sudah ada di store (misal dari login / localStorage)
    const prevPerms = Array.isArray(user.value?.permissions)
      ? user.value.permissions
      : []

    // 🔹 permissions yang dikirim API di call ini
    const permsFromApi = Array.isArray(raw.permissions)
      ? raw.permissions
      : null

    // 🔹 kalau API kirim permissions → pakai itu
    //    kalau TIDAK kirim → pakai yang lama (prevPerms)
    const mergedPerms = permsFromApi ?? prevPerms

    user.value = {
      id: raw.id || raw.user_id || raw.id_user || null,
      name: raw.name || raw.nama || raw.username || null,
      email: raw.email || null,
      file_user: avatar_url,
      permissions: mergedPerms,
      roles: Array.isArray(raw.roles) ? raw.roles : [],
      ...raw,
    }

    localStorage.setItem('user', JSON.stringify(user.value))
  }


  // const initSession = async () => {
  //   const storedToken = localStorage.getItem("token")
  //   const storedUser = localStorage.getItem("user")

  //   console.log('[auth] initSession start', {
  //     storedTokenExists: !!storedToken,
  //     storedUserExists: !!storedUser,
  //   })

  //   if (!storedToken) return false

  //   token.value = storedToken
  //   api.defaults.headers.common.Authorization = `Bearer ${storedToken}`

  //   // 1️⃣ Hydrate user dari localStorage dulu → permissions KEISI duluan
  //   if (storedUser) {
  //     try {
  //       const parsedUser = JSON.parse(storedUser)
  //       setUser(parsedUser)
  //     } catch (e) {
  //       console.warn('[auth] gagal parse storedUser, reset session', e)
  //       token.value = null
  //       user.value = null
  //       localStorage.removeItem("token")
  //       localStorage.removeItem("user")
  //       delete api.defaults.headers.common.Authorization
  //       return false
  //     }
  //   }

  //   // 2️⃣ (opsional) refresh profil dari server, TANPA ngereset permissions
  //   try {
  //     const { data: listData } = await api.get("/publik/user-me")
  //     console.log('[auth] /publik/user-me list response', listData)

  //     if (!listData?.status || !Array.isArray(listData?.data)) {
  //       throw new Error('Invalid user list response')
  //     }

  //     const userId = listData.data[0]?.id
  //     if (!userId) throw new Error('User ID not found')

  //     const { data: detailData } = await api.get(`/publik/user-me/${userId}`)
  //     console.log('[auth] /publik/user-me/:id response', detailData)

  //     if (detailData?.status && detailData?.data) {
  //       setUser(detailData.data)   // ⬅️ sekarang aman: setUser tidak menghapus permissions lama
  //       return true
  //     }

  //     throw new Error('Invalid user detail data')
  //   } catch (error) {
  //     console.error('[auth] initSession error:', error)
  //     token.value = null
  //     user.value = null
  //     localStorage.removeItem("token")
  //     localStorage.removeItem("user")
  //     delete api.defaults.headers.common.Authorization
  //     return false
  //   }
  // }

  // ...existing code...

  // const login = async (credentials) => {
  //   loading.value = true;
  //   try {
  //     console.log('[auth] login attempt');
  //     const { data } = await api.post("/auth/login", credentials);

  //     if (!data?.token) {
  //       throw new Error('No token received');
  //     }

  //     // Set token first
  //     token.value = data.token;
  //     localStorage.setItem("token", data.token);
  //     api.defaults.headers.common.Authorization = `Bearer ${data.token}`;

  //     // Ambil detail user dengan ID yang benar
  //     const userId = data.user?.id || data.data?.id;
  //     if (!userId) throw new Error('User ID not found in login response');

  //     // Fetch detail user untuk dapat avatar
  //     const { data: detailData } = await api.get(`/publik/user-me/${userId}`);
  //     console.log('[auth] User detail after login:', detailData);

  //     if (detailData?.status && detailData?.data) {
  //       setUser(detailData.data);
  //       console.log('[auth] login success with avatar', { 
  //         id: user.value?.id, 
  //         name: user.value?.name,
  //         avatar: user.value?.file_user 
  //       });
  //     } else {
  //       throw new Error('Failed to get user details');
  //     }

  //     return { success: true };
  //   } catch (error) {
  //     console.error('[auth] login failed:', error);
  //     token.value = null;
  //     user.value = null;
  //     localStorage.removeItem("token");
  //     localStorage.removeItem("user");
  //     delete api.defaults.headers.common.Authorization;
  //     throw error;
  //   } finally {
  //     loading.value = false;
  //   }
  // };
const initSession = async () => {
  const storedToken = localStorage.getItem("token");
  const storedUserJson = localStorage.getItem("user");

  if (!storedToken) return false;

  token.value = storedToken;
  api.defaults.headers.common.Authorization = `Bearer ${storedToken}`;

  // 1. Pulihkan user dari localStorage dulu
  if (storedUserJson) {
    try {
      const parsed = JSON.parse(storedUserJson);
      setUser(parsed);
    } catch (e) {
      console.error('[auth] gagal parse user dari localStorage', e);
    }
  }

  const userId = user.value?.id;
  if (!userId) return false;

  // 2. Opsional: refresh profile terbaru dari server, pakai ID yang sudah kita tahu
  try {
    const { data: detailData } = await api.get(`/publik/user-me/${userId}`);
    if (detailData?.status && detailData?.data) {
      setUser(detailData.data);
      return true;
    }
  } catch (error) {
    console.error('[auth] initSession error:', error);
  }

  return !!user.value?.id;
};

  // const login = async (credentials) => {
  //   loading.value = true;
  //   try {
  //     console.log('[auth] login attempt');
  //     const { data } = await api.post("/auth/login", credentials);

  //     if (!data?.token) {
  //       throw new Error('No token received');
  //     }

  //     // Set token dulu
  //     token.value = data.token;
  //     localStorage.setItem("token", data.token);
  //     api.defaults.headers.common.Authorization = `Bearer ${data.token}`;

  //     // 🔥 1) LANGSUNG setUser dari response login (sudah ada permissions)
  //     if (data.user) {
  //       setUser(data.user);
  //     }

  //     // 🔥 2) (opsional) ambil detail tambahan dari /publik/user-me/:id
  //     const userId = data.user?.id || data.data?.id;
  //     if (userId) {
  //       const { data: detailData } = await api.get(`/publik/user-me/${userId}`);
  //       console.log('[auth] User detail after login:', detailData);

  //       if (detailData?.status && detailData?.data) {
  //         // setUser lagi → sekarang tidak akan menghapus permissions yang sudah ada
  //         setUser(detailData.data);
  //       }
  //     }

  //     console.log('[auth] login success', {
  //       id: user.value?.id,
  //       name: user.value?.name,
  //       permissions: user.value?.permissions,
  //     });

  //     return { success: true };
  //   } catch (error) {
  //     console.error('[auth] login failed:', error);
  //     token.value = null;
  //     user.value = null;
  //     localStorage.removeItem("token");
  //     localStorage.removeItem("user");
  //     delete api.defaults.headers.common.Authorization;
  //     throw error;
  //   } finally {
  //     loading.value = false;
  //   }
  // };


  // ...existing code...
  // const logout = () => {
  //   token.value = null
  //   user.value = null
  //   localStorage.removeItem("token")
  //   localStorage.removeItem("user")
  //   delete api.defaults.headers.common.Authorization
  //   router.replace({ name: "Login" })
  // }

  // Trusted device token helpers
  const getTrustedDeviceToken = () => {
    return localStorage.getItem('X-Trusted-Device') || null
  }

  const setTrustedDeviceToken = (token) => {
    if (!token) {
      localStorage.removeItem('X-Trusted-Device')
    } else {
      localStorage.setItem('X-Trusted-Device', token)
    }
  }

  const clearTrustedDeviceToken = () => {
    localStorage.removeItem('X-Trusted-Device')
  }

  const setForceChangePassword = (val = true) => {
    forceChangePassword.value = Boolean(val)
  }

  const clearForceChangePassword = () => {
    forceChangePassword.value = false
  }

  const setToken = (newToken) => {
    if (!newToken) {
      token.value = null
      localStorage.removeItem('token')
      delete api.defaults.headers.common.Authorization
    } else {
      token.value = newToken
      localStorage.setItem('token', newToken)
      api.defaults.headers.common.Authorization = `Bearer ${newToken}`
    }
  }

const applyAuthSession = async (data) => {
  // data diharapkan punya token + user (atau minimal token)
  if (!data?.token) throw new Error('No token received')

  token.value = data.token
  localStorage.setItem('token', data.token)
  api.defaults.headers.common.Authorization = `Bearer ${data.token}`

  if (data.user) setUser(data.user)

  // jika server mengembalikan trusted_device sebagai objek, ambil tokennya
  const trustedToken =
    data?.trusted_device?.token ||
    // data?.trusted_token ||
    // data?.trustedToken ||
    // data?.trusted_device_token ||
    // data?.trusted ||
    // data?.device_token ||
    // data?.x_trusted_token ||
    null

  if (trustedToken) {
    setTrustedDeviceToken(trustedToken)
  }

  // opsional: refresh detail user
  const userId = data.user?.id || data.data?.id
  if (userId) {
    try {
      const { data: detailData } = await api.get(`/publik/user-me/${userId}`)
      if (detailData?.status && detailData?.data) setUser(detailData.data)
    } catch (e) {
      console.error('[auth] user-me fetch failed:', e)
      
    }
  }

  return true
}

const extractMessage = (error) => {
  const msg =
    error?.response?.data?.message ||
    error?.response?.data?.error ||
    error?.message ||
    'Terjadi kesalahan'
  return typeof msg === 'string' ? msg : JSON.stringify(msg)
}

// --- REPLACE login() kamu dengan ini ---
  const login = async (credentials) => {
  loading.value = true
  try {
    const trustedToken = getTrustedDeviceToken()

    // include X-Trusted-Device header only when token is present
    const headers = {}
    if (trustedToken) headers['X-Trusted-Device'] = trustedToken

    const { data } = await api.post('/auth/login', credentials, { headers })

    // CASE A: backend kasih token -> login selesai
    if (data?.token) {
      await applyAuthSession(data)
      return { success: true }
    }

    // CASE B: backend minta MFA -> buka modal, jangan clear session
    const mfaId =
      data?.mfa_id ||
      data?.mfaId ||
      data?.data?.mfa_id ||
      data?.data?.mfaId

    if (mfaId) {
      return {
        success: false,
        mfaRequired: true,
        mfa_id: Number(mfaId),
        message: data?.message || 'Verifikasi OTP diperlukan.',
      }
    }

    // fallback: tidak ada token & tidak ada mfa_id
    return {
      success: false,
      message: data?.message || 'Login gagal.',
    }
  } catch (error) {
    console.error('[auth] login failed:', error)
    // untuk flow MFA, kita *tidak* auto clear token di sini,
    // karena kemungkinan memang belum ada token dari awal.
    return { success: false, message: extractMessage(error) }
  } finally {
    loading.value = false
  }
}

// --- ADD method baru ini ---
const verifyMfa = async (payload) => {
  loading.value = true
  try {
    const { data } = await api.post('/auth/login/mfa-verify', payload)

    if (!data?.token) {
      return { success: false, message: data?.message || 'OTP tidak valid.' }
    }

    await applyAuthSession(data)

    // jika backend mengembalikan token trusted device, simpan ke localStorage
    const trustedToken =
      data?.trusted_device?.token ||
      data?.trusted_token ||
      data?.trustedToken ||
      data?.trusted_device_token ||
      data?.trusted ||
      data?.device_token ||
      data?.x_trusted_token ||
      null

    if (trustedToken) {
      setTrustedDeviceToken(trustedToken)
    } else if (payload?.remember_device === true) {
      // jika payload meminta remember_device tapi server tidak mengembalikan token,
      // jangan simpan nilai palsu — biarkan caller menangani UX atau server memperbaiki response.
    }

    return { success: true }
  } catch (error) {
    console.error('[auth] verifyMfa failed:', error)
    return { success: false, message: extractMessage(error) }
  } finally {
    loading.value = false
  }
}

  const logout = async () => {
  try {
   
    if (token.value) {
      await api.get('/auth/logout') 
      console.log('[auth] logout successful on server')
    }
  } catch (error) {
    console.error('[auth] logout error:', error?.response?.data || error)
    // jangan throw, supaya tetap lanjut bersihkan sisi FE
  } finally {
    token.value = null
    user.value = null
    localStorage.removeItem('token')
    localStorage.removeItem('user')
    delete api.defaults.headers.common.Authorization

    router.replace({ name: 'Login' })
  }
}

  return {
    user,
    token,
    forceChangePassword,
    loading,
    isLoggedIn,
    hasPermission,
    setUser,
    initSession,
    logout,
    login,
    verifyMfa,
    getTrustedDeviceToken,
    setTrustedDeviceToken,
    clearTrustedDeviceToken,
    setForceChangePassword,
    clearForceChangePassword,
    setToken,

  }
})